// Los anexos de la evaluación: el documento que sube un especialista invitado (HU-43-C10)
// y el archivo de sustento de la ampliación (HU-49-C2). Son el MISMO archivo para el
// servidor —`PoliticaDeAnexos`, alimentada por las filas `adjunto_*` de `parametro`— así
// que las dos pantallas tienen que rechazarlo por lo mismo y con las mismas palabras.
//
// Antes no era así: la tarjeta de aportes pre-validaba y la de sustento no, de modo que
// el mismo .exe se rechazaba en una pantalla al elegirlo y en la otra recién al guardar,
// con el texto crudo del 422 (operador, 22-sep-2026). Este módulo es la regla única.
//
// LOS LÍMITES LLEGAN DEL SERVIDOR, no se escriben aquí: si no vienen, no se pre-valida y
// contesta él. Un número inventado en la pantalla es peor que no decir nada — anuncia un
// tope que no es el de la base.

import { tamanoLegible } from '../components/ui';

/** Lo que el servidor publica de la política de anexos. TODOS los campos son opcionales. */
export type LimitesDeAnexo = {
  anexo_extensiones_bloqueadas?: string[];
  anexo_tamano_maximo_mb?: number | null;
  /**
   * HU-49-C2 · cuántos archivos caben en UNA entrada del sustento. Llega con la ampliación
   * (`parametro`), no con la evaluación, porque es un cupo del sustento y no del caso. Sin
   * él no se pre-valida el cupo: contesta el servidor.
   */
  anexo_cantidad_maxima?: number | null;
};

/** Lo que hace falta saber de un archivo ya adjunto para juzgar el siguiente. */
export type AnexoYaAdjunto = { nombre_archivo: string };

export const BYTES_POR_MB = 1048576;

/** El nombre como se compara: sin espacios de sobra y sin mayúsculas. */
export const nombreComparable = (nombre: string): string => nombre.trim().toLowerCase();

/** La extensión en minúsculas y sin punto, como la calcula el servidor. */
export const extensionDe = (nombre: string): string => {
  const punto = nombre.lastIndexOf('.');
  return punto < 0 ? '' : nombre.slice(punto + 1).trim().toLowerCase();
};

/** El cupo lleno, dicho igual aquí y en el botón que se apaga por su culpa. */
export const mensajeDeCupoLleno = (cuantos: number, cupo: number): string =>
  `La entrada ya tiene ${cuantos} anexos y el máximo es ${cupo}. Quita uno para adjuntar otro.`;

/**
 * Por qué este archivo NO se puede adjuntar, o `null` si cabe.
 *
 * El orden es el de `PoliticaDeAnexos.verificar` en el servidor: formato, cupo, vacío,
 * tamaño. Que coincida importa — si cada lado corta por donde quiere, el mismo archivo se
 * rechaza con un motivo en la pantalla y con otro al guardar.
 *
 * `yaAdjuntos` son los archivos que YA cuelgan de la misma entrada (HU-49-C2, 22-sep-2026):
 * de ellos salen el cupo y el duplicado por nombre. Vacío —o sin cupo configurado— equivale
 * a no comprobarlos.
 */
export function problemaDelAnexo(archivo: File, limites: LimitesDeAnexo, yaAdjuntos: AnexoYaAdjunto[] = []): string | null {
  const extension = extensionDe(archivo.name);
  if ((limites.anexo_extensiones_bloqueadas ?? []).includes(extension)) {
    return `No se pueden adjuntar archivos ejecutables («.${extension}»). Se acepta cualquier otro formato: documento, imagen, video o comprimido.`;
  }
  const cupo = limites.anexo_cantidad_maxima;
  if (cupo && yaAdjuntos.length >= cupo) {
    return mensajeDeCupoLleno(yaAdjuntos.length, cupo);
  }
  // `=== 0` y no `!archivo.size`: un doble de prueba sin `size` no debe leerse como vacío.
  if (archivo.size === 0) {
    return `«${archivo.name}» está vacío (0 bytes). Vuelve a exportarlo y adjúntalo de nuevo.`;
  }
  const tope = limites.anexo_tamano_maximo_mb;
  if (tope && archivo.size > tope * BYTES_POR_MB) {
    return `«${archivo.name}» pesa ${tamanoLegible(archivo.size)} y el máximo por archivo es ${tope} MB.`;
  }
  // El duplicado NO lo juzga el servidor: es una ayuda de la pantalla, y por eso va al
  // final —primero lo que de verdad va a rechazarse—. Se compara por nombre porque es lo
  // único que se conoce del que ya está; el duplicado por contenido exige la huella.
  if (yaAdjuntos.some((a) => nombreComparable(a.nombre_archivo) === nombreComparable(archivo.name))) {
    return `«${archivo.name}» ya está adjunto en esta entrada. Quítalo antes de volver a subirlo, o cámbiale el nombre si es otra versión.`;
  }
  return null;
}

/** Los límites vigentes dichos en la pantalla; vacío si el servidor no los envía. */
export const textoDeLosLimites = (limites: LimitesDeAnexo): string => {
  const partes: string[] = [];
  if (limites.anexo_cantidad_maxima) partes.push(`hasta ${limites.anexo_cantidad_maxima} archivos por entrada`);
  if (limites.anexo_tamano_maximo_mb) partes.push(`máximo ${limites.anexo_tamano_maximo_mb} MB por archivo`);
  if ((limites.anexo_extensiones_bloqueadas ?? []).length) partes.push('cualquier formato salvo ejecutables');
  return partes.join(' · ');
};
